import ChevronRight from '@mui/icons-material/ChevronRight'
import Done from '@mui/icons-material/Done'
import Error from '@mui/icons-material/Error'
import { Divider, ListSubheader, Paper, Skeleton, Stack, Typography } from '@mui/material'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { TraitId, TraitStatus } from '@cibo/core'
import { FriendlyError, useFormFactor } from '@cibo/ui'

export type DetailsChecklistProps = {
  focusTraitId?: TraitId
  onSelectTrait(traitId: TraitId): void
  traitStatuses?: TraitStatus[]
  isLoading: boolean
  error?: any
  hideLegend?: boolean
}

export const DetailsChecklist = ({
  focusTraitId,
  onSelectTrait,
  traitStatuses,
  isLoading,
  error,
  hideLegend,
}: DetailsChecklistProps) => {
  const { t } = useTranslation(['detailsChecklist', 'traits'])
  const { isMobile } = useFormFactor()

  if (error) {
    return <FriendlyError message={t('errorLoading')} />
  }

  return (
    <Stack spacing={2}>
      <Paper>
        <List
          sx={{ padding: 0 }}
          aria-label={t('enrollmentProgress')}
          dense={false}
          subheader={<ListSubheader>{t('requiredInformation')}</ListSubheader>}
        >
          <Divider />
          {!!isLoading ? (
            <>
              <ListItemButton disabled>
                <Skeleton width={150} />
              </ListItemButton>
              <ListItemButton disabled>
                <Skeleton width={100} />
              </ListItemButton>
              <ListItemButton disabled>
                <Skeleton width={120} />
              </ListItemButton>
            </>
          ) : (
            traitStatuses?.map(({ traitId, missing, failing, missingOptional }, index) => (
              <Fragment key={traitId}>
                <ListItemButton
                  data-testid={`editor-selection-${traitId}`}
                  selected={!isMobile && traitId === focusTraitId}
                  onClick={() => onSelectTrait(traitId)}
                  dense
                >
                  <ListItemText primary={t(traitId, { ns: 'traits' })} />
                  {!!failing ? (
                    <Error color="error" />
                  ) : !missing && !missingOptional ? (
                    <Done color="success" />
                  ) : null}
                  {isMobile && <ChevronRight />}
                </ListItemButton>

                {isMobile && traitStatuses && index < traitStatuses.length - 1 && <Divider />}
              </Fragment>
            ))
          )}
        </List>
      </Paper>
      {!hideLegend && (
        <Stack direction="row" spacing={2} padding={1} flexWrap="wrap" justifyContent="flex-end">
          <Stack direction="row" spacing={1} alignItems="center">
            <Done color="success" fontSize="small" />
            <Typography variant="caption">{t('complete')}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Error color="error" fontSize="small" />
            <Typography variant="caption">{t('fail')}</Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
